import {
  getListCities,
  getListClientsCitiesStates,
  getListStates,
} from 'helpers/genericHelper';
import { getUnitStatusOptions } from '~/helpers/unitStatus';
import { getUserProfile } from 'helpers/userProfile';
import {
  ReactElement, useEffect, useMemo, useState,
} from 'react';
import { t } from 'i18next';

import { FilterButton } from '~/pages/NewOverview/components/Filter/FilterButton';
import { Option, Select } from '~/pages/NewOverview/components/SelectField';
import { ContainerClearFilters } from '~/pages/NewOverview/components/SelectField/styles';
import { UnitStatusKey } from '~/providers/types/unit';
import { EvaporatorIcon } from '~/icons/sidebar';
import { SnowflakeIcon, XIcon } from 'icons';

import { getMaintenanceFilterOptions } from './helpers/api';
import { MaintenanceFilters, MaintenanceViewBy, emptyMaintenanceFilters } from './types';
import {
  HeaderBar,
  HeaderTopRow,
  FieldsRow,
  FilterButtonSlot,
  ViewByGroup,
  ViewByLabel,
  ViewByOption,
  RadioOuter,
  RadioInner,
  ViewByIcon,
  ViewByText,
} from './styles';

interface MaintenanceHeaderProps {
  filters: MaintenanceFilters;
  onFiltersChange: (next: MaintenanceFilters) => void;
  viewBy: MaintenanceViewBy;
  onChangeViewBy: (value: MaintenanceViewBy) => void;
}

export function MaintenanceHeader({
  filters,
  onFiltersChange,
  viewBy,
  onChangeViewBy,
}: Readonly<MaintenanceHeaderProps>): ReactElement {
  const [profile] = useState(getUserProfile);
  const [loading, setLoading] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);

  const [baseStates, setBaseStates] = useState<{ value: string; name: string }[]>([]);
  const [baseCities, setBaseCities] = useState<{ value: string; name: string; stateId: number }[]>([]);
  const [baseClients, setBaseClients] = useState<{ value: string; name: string; cities: string[]; states: number[] }[]>([]);
  const [brandOptions, setBrandOptions] = useState<Option[]>([]);
  const [machineTypeOptions, setMachineTypeOptions] = useState<Option[]>([]);
  const [fluidOptions, setFluidOptions] = useState<Option[]>([]);

  const hasMultipleClients = !!(profile.manageAllClients || profile.manageMultipleClients);

  const unitStatusOptions = useMemo<Option[]>(
    () => getUnitStatusOptions(profile)
      .filter((item) => item.value !== 'DEACTIVATED')
      .map((item) => ({
        label: item.name,
        value: item.value,
      })),
    [profile],
  );

  const clientOptions = useMemo<Option[]>(
    () => baseClients.map((item) => ({
      label: item.name,
      value: item.value,
      iconName: 'User1Icon',
    })),
    [baseClients],
  );

  const stateOptions = useMemo<Option[]>(() => {
    let list = baseStates;
    if (filters.clients.length > 0) {
      const allowedStateIds = new Set<number>();
      filters.clients.forEach((cid) => {
        baseClients.find((c) => Number(c.value) === cid)?.states.forEach((s) => allowedStateIds.add(s));
      });
      list = list.filter((s) => allowedStateIds.has(Number(s.value)));
    }
    return list.map((item) => ({
      label: item.name,
      value: item.value,
      iconName: 'MarkerPin1Icon',
    }));
  }, [baseStates, baseClients, filters.clients]);

  const cityOptions = useMemo<Option[]>(() => {
    let list = baseCities;
    if (filters.clients.length > 0) {
      const allowedCityIds = new Set<string>();
      filters.clients.forEach((cid) => {
        baseClients.find((c) => Number(c.value) === cid)?.cities.forEach((cityId) => allowedCityIds.add(cityId));
      });
      list = list.filter((c) => allowedCityIds.has(c.value));
    }
    if (filters.states.length > 0) {
      list = list.filter((c) => filters.states.includes(String(c.stateId)));
    }
    return [...list]
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }))
      .map((item) => ({
        label: item.name,
        value: item.value,
        iconName: 'MarkerPin1Icon',
      }));
  }, [baseCities, baseClients, filters.clients, filters.states]);

  async function loadBaseOptions(): Promise<void> {
    setLoading(true);
    try {
      const [statesList, citiesList, clientsListRaw] = await Promise.all([
        getListStates(),
        getListCities(),
        hasMultipleClients ? getListClientsCitiesStates() : Promise.resolve([]),
      ]);

      setBaseStates(statesList);
      setBaseCities(citiesList);

      if (hasMultipleClients) {
        let allowed = clientsListRaw;
        if (!profile.manageAllClients) {
          const ids = new Set(profile.permissions.PER_CLIENT.map((c) => c.clientId));
          allowed = clientsListRaw.filter((c) => ids.has(Number(c.value)));
        }
        setBaseClients(allowed);
      }
    } finally {
      setLoading(false);
    }
  }

  async function loadMachineFilterOptions(): Promise<void> {
    const response = await getMaintenanceFilterOptions(filters);
    if (!response) return;
    setBrandOptions(response.BRANDS.map((item) => ({ label: item.label, value: item.value })));
    setMachineTypeOptions(response.TYPES.map((item) => ({ label: item.label, value: item.value })));
    setFluidOptions(response.FLUIDS.map((item) => ({ label: item.label, value: item.value })));
  }

  useEffect(() => {
    loadBaseOptions();
  }, []);

  useEffect(() => {
    loadMachineFilterOptions();
  }, [
    filters.clients,
    filters.states,
    filters.cities,
    filters.unitStatuses,
    filters.brands,
    filters.machineTypes,
    filters.fluids,
  ]);

  function setField<K extends keyof MaintenanceFilters>(key: K, value: MaintenanceFilters[K]): void {
    onFiltersChange({ ...filters, [key]: value });
  }

  type StringFieldKey = 'brands' | 'machineTypes' | 'fluids';

  function onToggleFilter(type: StringFieldKey) {
    return (value: string) => {
      const current = filters[type];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      setField(type, next);
    };
  }

  function onToggleClient(value: string): void {
    const id = Number(value);
    const next = filters.clients.includes(id)
      ? filters.clients.filter((v) => v !== id)
      : [...filters.clients, id];
    onFiltersChange({
      ...filters, clients: next, states: [], cities: [],
    });
  }

  function onToggleState(value: string): void {
    const next = filters.states.includes(value)
      ? filters.states.filter((v) => v !== value)
      : [...filters.states, value];
    onFiltersChange({ ...filters, states: next, cities: [] });
  }

  function onToggleCity(value: string): void {
    const next = filters.cities.includes(value)
      ? filters.cities.filter((v) => v !== value)
      : [...filters.cities, value];
    setField('cities', next);
  }

  function onToggleUnitStatus(value: string): void {
    const key = value as UnitStatusKey;
    const next = filters.unitStatuses.includes(key)
      ? filters.unitStatuses.filter((v) => v !== key)
      : [...filters.unitStatuses, key];
    setField('unitStatuses', next);
  }

  const hasSelectedFilters = [
    filters.clients.length,
    filters.unitStatuses.length,
    filters.states.length,
    filters.cities.length,
    filters.brands.length,
    filters.machineTypes.length,
    filters.fluids.length,
  ].some((count) => count > 0);

  function onClickClearButton(): void {
    onFiltersChange(emptyMaintenanceFilters);
  }

  return (
    <HeaderBar>
      <HeaderTopRow>
        <FilterButtonSlot disabled={loading}>
          <FilterButton
            collapsed={isCollapsed}
            onClick={() => setIsCollapsed((prev) => !prev)}
            disabled={loading}
          />
        </FilterButtonSlot>
        <ViewByGroup>
          <ViewByLabel disabled={loading}>{t('visualizarGraficosPor')}</ViewByLabel>
          <ViewByOption
            type="button"
            selected={viewBy === 'machines'}
            onClick={() => onChangeViewBy('machines')}
            disabled={loading}
          >
            <RadioOuter selected={viewBy === 'machines'} disabled={loading}>
              {viewBy === 'machines' && <RadioInner disabled={loading} />}
            </RadioOuter>
            <ViewByIcon>
              <EvaporatorIcon size={16} />
            </ViewByIcon>
            <ViewByText>{t('quantidadeDeMaquinas')}</ViewByText>
          </ViewByOption>
          <ViewByOption
            type="button"
            selected={viewBy === 'capacity'}
            onClick={() => onChangeViewBy('capacity')}
            disabled={loading}
          >
            <RadioOuter selected={viewBy === 'capacity'} disabled={loading}>
              {viewBy === 'capacity' && <RadioInner disabled={loading} />}
            </RadioOuter>
            <ViewByIcon>
              <SnowflakeIcon size="16" />
            </ViewByIcon>
            <ViewByText>{t('capacidadeRefrigeracao')}</ViewByText>
          </ViewByOption>
        </ViewByGroup>
      </HeaderTopRow>
      {!isCollapsed && (
        <FieldsRow>
          {hasMultipleClients && (
            <Select
              icon="User1Icon"
              placeholder={t('cliente')}
              options={clientOptions}
              multiple
              value={filters.clients.map(String)}
              onSelect={onToggleClient}
              disabled={loading}
              isSearchable
              dataTestId="maintenance-filter-cliente"
            />
          )}
          <Select
            icon="BookmarkIcon"
            placeholder={t('statusUnidade')}
            options={unitStatusOptions}
            multiple
            value={filters.unitStatuses}
            onSelect={onToggleUnitStatus}
            disabled={loading}
            dataTestId="maintenance-filter-status-unidade"
          />
          <Select
            icon="MarkerPin1Icon"
            placeholder={t('estado')}
            options={stateOptions}
            multiple
            value={filters.states}
            onSelect={onToggleState}
            disabled={loading}
            isSearchable
            dataTestId="maintenance-filter-estado"
          />
          <Select
            icon="MarkerPin1Icon"
            placeholder={t('cidade')}
            options={cityOptions}
            multiple
            value={filters.cities}
            onSelect={onToggleCity}
            disabled={loading}
            isSearchable
            dataTestId="maintenance-filter-cidade"
          />
          <Select
            icon="Building3Icon"
            placeholder={t('fabricante')}
            options={brandOptions}
            multiple
            value={filters.brands}
            onSelect={onToggleFilter('brands')}
            disabled={loading}
            isSearchable
            dataTestId="maintenance-filter-fabricante"
          />
          <Select
            icon="LayoutGrid1Icon"
            placeholder={t('tipoMaquina')}
            options={machineTypeOptions}
            multiple
            value={filters.machineTypes}
            onSelect={onToggleFilter('machineTypes')}
            disabled={loading}
            isSearchable
            dataTestId="maintenance-filter-tipo-maquina"
          />
          <Select
            icon="SnowflakeIcon"
            placeholder={t('fluidoRefrigerante')}
            options={fluidOptions}
            multiple
            value={filters.fluids}
            onSelect={onToggleFilter('fluids')}
            disabled={loading}
            isSearchable
            dataTestId="maintenance-filter-fluido"
          />
          {hasSelectedFilters && (
            <ContainerClearFilters
              onClick={onClickClearButton}
              data-testid="maintenance-clear-filters"
            >
              <XIcon />
            </ContainerClearFilters>
          )}
        </FieldsRow>
      )}
    </HeaderBar>
  );
}
